'use client';

import { useLinkStatus } from 'next/link';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { LoadingOverlay } from '@/components/Load/loading-overlay';

interface NavProgressContextType {
  // 遷移中のリンク数(複数同時遷移に備えてカウントで管理)
  inc: () => void;
  dec: () => void;
}

const NavProgressContext = createContext<NavProgressContextType | null>(null);

// 一瞬で終わる遷移(チャンク取得済み)でオーバーレイがチラつかないよう、
// この時間以上かかる遷移のみブランドローダーを表示する。
const SHOW_DELAY_MS = 150;

/**
 * 画面切り替え(ルート遷移)中、ブランドローダーのオーバーレイを表示する。
 * 遷移先のJSチャンク/RSC取得中は画面が無反応になりラグに見えるため、
 * クリック直後から視覚フィードバックを出す。
 *
 * 一覧ページのSuspense fallback(LoadingOverlay)と同じ見た目で統一する。
 * 遷移検知はNext公式の useLinkStatus を使う(<NavLinkProgress />)。
 * ルート全体を包むよう、ルートレイアウトで本Providerを使用する。
 */
export function NavProgressProvider({ children }: { children: ReactNode }) {
  const [pendingCount, setPendingCount] = useState(0);
  const [show, setShow] = useState(false);

  const inc = useCallback(() => setPendingCount((c) => c + 1), []);
  const dec = useCallback(() => setPendingCount((c) => Math.max(0, c - 1)), []);

  useEffect(() => {
    if (pendingCount === 0) {
      setShow(false);
      return;
    }
    const timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pendingCount]);

  return (
    <NavProgressContext.Provider value={{ inc, dec }}>
      <LoadingOverlay isLoading={show} />
      {children}
    </NavProgressContext.Provider>
  );
}

/**
 * <Link> の子として配置し、その遷移状態を進捗表示へ反映する。
 * useLinkStatus は <Link> の子孫でのみ動作する。
 */
export function NavLinkProgress() {
  const { pending } = useLinkStatus();
  const ctx = useContext(NavProgressContext);

  useEffect(() => {
    if (!ctx || !pending) return;
    ctx.inc();
    return () => ctx.dec();
  }, [pending, ctx]);

  return null;
}
